import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { type InputHTMLAttributes, useState } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * RHF-aware password input with show/hide toggle. Reads the parent
 * `useFormContext()` and registers itself by `name`. Use inside a
 * `<FormProvider>` wrapping your `useForm()` instance, OR pass `register`
 * explicitly via `{...register('password')}` on a plain `<input>` if you
 * don't want the context lookup.
 */
interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'name'> {
    name: string;
    className?: string;
}

export function PasswordField({ name, className = '', ...inputProps }: PasswordFieldProps) {
    const { register } = useFormContext();
    const [
        showPassword,
        setShowPassword,
    ] = useState(false);

    return (
        <div className='relative w-full'>
            <input
                className={ clsx(className, 'pr-10') }
                type={ showPassword ? 'text' : 'password' }
                { ...inputProps }
                { ...register(name) }
            />
            <button
                aria-label={ showPassword ? 'Hide password' : 'Show password' }
                className='absolute inset-y-0 right-0 z-10 flex items-center pr-3 text-muted-foreground hover:text-foreground'
                onMouseDown={ (e) => {
                    e.preventDefault();
                    setShowPassword((v) => {
                        return !v;
                    });
                } }
                tabIndex={ -1 }
                type='button'
            >
                { showPassword
                    ? <EyeSlashIcon className='h-4 w-4' />
                    : <EyeIcon className='h-4 w-4' />
                }
            </button>
        </div>
    );
}

// FieldWithLabel: opt into the same border/bg/padding chrome it gives the
// default <input>. PasswordField wraps a real input that needs to look like one.
PasswordField.usesInputChrome = true;
